// Ende eines Runs (M9, Fabi 29.10.2026; Erste Fassung, Runde 7 mit den Bildern des Grafik-Leads in public/art/ende/):
// Nach einem gewonnenen Run kommt das Boot am Meer an (Ebenen aus meer.json, das Boot des Runs mit der Truppe auf ihren
// Posten), darunter die Logbuch-Seite mit dem Satz zum Ändern und den drei Dingen von der Reise, und die Bibliothek als
// Regal mit einem Buchrücken je Reise. Reine Darstellung; der Ordner gehört dem Grafik-Lead, hier wird nur gelesen.
import type { RunRecord, RunState } from '../sim/index.ts';
import { drawBoat, PAD, rockAt } from './fahrt/boat.ts';
import { aboardOf } from './fahrt/crew.ts';
import { img, loadBoote, loadFahrt } from './fahrt/data.ts';
import { boatArt } from './fahrt/index.ts';
import { rockOf, swayAt } from './fahrt/rock.ts';
import { presentScene, sceneCanvas } from './pixel.ts';

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

type Meer = {
  breite: number;
  hoehe: number;
  rand: number;
  wl: number;
  ebenen: { id: string; datei?: string; tempo?: number; bilder?: number; ms?: number }[];
  boot: { bootTon: string; lichter: number };
  ankunft: { dauer: number };
  wolken: { drift: number; ausblenden: number };
  lichter: [number, number, string][];
  rauch: [number, number];
};
type Buch = { id: string; datei: string; breite: number; hoehe: number; feld: [number, number, number, number]; schrift: string };
type Regal = {
  regal: { groesse: [number, number]; faecher: [number, number, number, number][]; boden_y: number[]; schild: [number, number, number, number]; laterne: [number, number] };
  buecher: Buch[];
  buchstuetze: { datei: string; breite: number; hoehe: number };
  lesezeichen: string[];
};
type Logbuch = { text: { links: number[]; rechts: number[] }; zeilen: { titel_y: number; text_y: number; zeilenhoehe: number }; linien: { schrift: string; schrift_leise: string } };

const data: { meer: Meer | null; regal: Regal | null; logbuch: Logbuch | null } = { meer: null, regal: null, logbuch: null };
let loading = false;
/** Die Beschreibungen in ende/ laden; null, solange sie fehlen */
export function loadEnde(): typeof data {
  if (!loading && typeof fetch === 'function') {
    loading = true;
    for (const k of ['meer', 'regal', 'logbuch'] as const)
      void fetch(`./art/ende/${k}.json`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => ((data as Record<string, unknown>)[k] = d))
        .catch(() => {});
  }
  return data;
}

// ---------- Ankunft am Meer ----------

/** Welche Ebene ein Licht trägt (es zieht beim Einlaufen mit ihr) */
const LIGHT_LAYER: Record<string, string> = { leuchtturm: 'mitte', fenster: 'ufer', laterne: 'dinge' };

/** Ein Bild der Ankunft, t Sekunden nach dem Erscheinen; false, solange die Bilder laden */
export function drawArrival(canvas: HTMLCanvasElement, run: RunState, t: number): boolean {
  const m = loadEnde().meer;
  const boote = loadBoote();
  if (!m || !boote) return false;
  const W = m.breite - 2 * m.rand;
  const scene = sceneCanvas('meer', W, m.hoehe);
  const ctx = scene.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, W, m.hoehe);
  // Ankommen: alle Ebenen liegen um tempo × rand nach rechts und gleiten in ankunft.dauer Sekunden auf 0
  const k = Math.min(1, Math.max(0, t / m.ankunft.dauer));
  const ease = 1 - (1 - k) ** 3;
  const shift = (tempo: number) => tempo * m.rand * (1 - ease);
  const tempoOf = (id: string) => m.ebenen.find((e) => e.id === id)?.tempo ?? 0;
  const art = boatArt(run);
  const boat = boote.boote[art] ?? Object.values(boote.boote)[0]!;
  // Runde 8: Das Boot schaukelt leise wie in der Fahrt bei ruhigem Wetter
  const spiel = loadFahrt()?.spiel;
  const calm = spiel?.sturmstufen.ruhig?.schaukeln;
  const rock = calm ? rockOf(swayAt(t * (spiel?.schaukeln?.takt ?? 0.92) * calm.tempo, calm), boat, spiel?.schaukeln?.grenzen[art]) : undefined;
  const bx = Math.floor(W / 2 - boat.w / 2);
  const by = m.wl - boat.wl;
  for (const e of m.ebenen) {
    if (e.id === 'boot') {
      // Das Boot des Runs mit der Truppe auf ihren Posten, im Abendlicht; die Spiegelung schaukelt mit
      // Steht niemand mehr am Steuer oder im Ausguck (Abschied unterwegs), übernehmen die, die es am besten können
      const aboard = run.posts?.kaeptn && run.posts.ausguck && run.crew.some((c) => c.id === run.posts!.kaeptn) && run.crew.some((c) => c.id === run.posts!.ausguck) ? run : { ...run, posts: undefined };
      const layers = drawBoat(bootCtx(boat.w + 2 * PAD, boat.h + 2 * PAD), { boat, view: 'quer', crew: aboardOf(aboard, boat), x: PAD, y: PAD, tone: m.boot.bootTon, now: t, chat: k >= 1, rock }, []);
      if (!layers) return false;
      ctx.save();
      ctx.globalAlpha = 0.28;
      ctx.translate(bx - PAD, m.wl + boat.wl + 1 + PAD);
      ctx.scale(1, -1);
      ctx.drawImage(layers.outside, 0, 0);
      ctx.restore();
      ctx.drawImage(layers.view, bx - PAD, by - PAD);
      for (const [lx, ly] of boat.lampen) {
        const [ox, oy] = rockAt(rock, lx, ly);
        glow(ctx, bx + lx + ox, by + ly + oy, 0.5 * m.boot.lichter + 0.1, t, lx);
      }
      continue;
    }
    if (!e.datei) continue;
    const im = img(`ende/${e.datei}`);
    if (!im) return false;
    let x = shift(e.tempo ?? 0) - m.rand;
    ctx.save();
    if (e.id === 'wolken') {
      // Die letzten Wolken des Unwetters ziehen nach links ab und blenden aus
      x += m.wolken.drift * t;
      ctx.globalAlpha = Math.max(0, 1 - t / m.wolken.ausblenden);
    }
    if (e.bilder && e.bilder > 1) {
      const f = Math.floor((t * 1000) / (e.ms ?? 240)) % e.bilder;
      ctx.drawImage(im, f * m.breite, 0, m.breite, m.hoehe, Math.round(x), 0, m.breite, m.hoehe);
    } else ctx.drawImage(im, Math.round(x), 0);
    ctx.restore();
    if (e.id === 'ufer') smoke(ctx, m.rauch[0] + shift(e.tempo ?? 0), m.rauch[1], t);
  }
  // Die Lichter flackern sanft; das Leuchtfeuer (Art leuchtturm) ist ein Feuer und flackert warm und unruhig (Runde 8)
  for (const [lx, ly, kind] of m.lichter) {
    const sx = lx + shift(tempoOf(LIGHT_LAYER[kind] ?? 'dinge'));
    const pulse = kind === 'leuchtturm' ? 0.8 + 0.12 * Math.sin(t * 9.7) + 0.1 * Math.sin(t * 5.3 + 1.3) * Math.sin(t * 13.1) : 0.85 + 0.15 * Math.sin(t * 7 + lx) * Math.sin(t * 3.3);
    glow(ctx, sx, ly, 0.35 * pulse, t, lx, kind === 'leuchtturm' ? 22 : 12);
  }
  presentScene(canvas, scene);
  return true;
}

let boatCanvas: HTMLCanvasElement | null = null;
function bootCtx(w: number, h: number): CanvasRenderingContext2D {
  boatCanvas ??= document.createElement('canvas');
  if (boatCanvas.width !== w || boatCanvas.height !== h) {
    boatCanvas.width = w;
    boatCanvas.height = h;
  }
  const g = boatCanvas.getContext('2d')!;
  g.clearRect(0, 0, w, h);
  return g;
}

function glow(ctx: CanvasRenderingContext2D, x: number, y: number, a: number, t: number, seed: number, r = 14): void {
  const f = 0.92 + 0.08 * Math.sin(t * 6.1 + seed);
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, `rgba(255, 214, 140, ${a * f})`);
  g.addColorStop(1, 'rgba(255, 214, 140, 0)');
  ctx.fillStyle = g;
  ctx.fillRect(x - r, y - r, 2 * r, 2 * r);
}

/** Ein dünner Rauchfaden, der gerade aufsteigt */
function smoke(ctx: CanvasRenderingContext2D, x: number, y: number, t: number): void {
  for (let i = 0; i < 6; i++) {
    const age = (t * 0.35 + i / 6) % 1;
    ctx.fillStyle = `rgba(215, 205, 195, ${0.3 * (1 - age)})`;
    ctx.beginPath();
    ctx.arc(x + Math.sin(age * 5 + i) * 1.5 - age * 3, y - age * 26, 1 + age * 2.5, 0, Math.PI * 2);
    ctx.fill();
  }
}

// ---------- Logbuch ----------

/** Die aufgeschlagene Logbuch-Seite: links der Eintrag zum Ändern, rechts die drei Dinge von der Reise */
export function logbookHtml(title: string, line: string, facts: string[]): string {
  const lb = loadEnde().logbuch;
  const L = lb?.text.links ?? [48, 29, 236, 303];
  const R = lb?.text.rechts ?? [330, 29, 226, 251];
  const tY = lb?.zeilen.titel_y ?? 29;
  const textY = lb?.zeilen.text_y ?? 51;
  const textH = L[1]! + L[3]! - textY;
  return `<div class="logbook" role="group" aria-label="Logbuch">
      <div class="lb-title" style="left:${L[0]}px;top:${tY}px;width:${L[2]}px">${esc(title)}</div>
      <textarea id="end-line" class="lb-text" maxlength="300" aria-label="Satz fürs Logbuch" style="left:${L[0]}px;top:${textY}px;width:${L[2]}px;height:${textH}px">${esc(line)}</textarea>
      <div class="lb-title" style="left:${R[0]}px;top:${tY}px;width:${R[2]}px">Drei Dinge von dieser Reise</div>
      <ol class="lb-facts" style="left:${R[0]}px;top:${textY}px;width:${R[2]}px;height:${R[1]! + R[3]! - textY}px">${facts.map((f) => `<li>${esc(f)}</li>`).join('')}</ol>
    </div>`;
}

// ---------- Bibliothek als Regal ----------

/** Welches Buch eine Reise bekommt: reihum nach ihrer Kennung, damit es immer dasselbe bleibt */
function bookOf(r: Regal, rec: RunRecord): Buch {
  let h = 5;
  for (const ch of rec.id) h = (h * 33 + ch.charCodeAt(0)) % 9973;
  return r.buecher[h % r.buecher.length]!;
}

type Placed = { rec: RunRecord; n: number; book: Buch; shelf: number; fach: number; x: number };

/** Bücher von links nach rechts und von oben nach unten einsortieren, ein Regal hat drei Fächer */
function layout(r: Regal, list: RunRecord[]): Placed[][] {
  const shelves: Placed[][] = [[]];
  let fach = 0;
  const F = r.regal.faecher;
  let x = F[0]![0] + 4;
  list.forEach((rec, i) => {
    const book = bookOf(r, rec);
    const end = F[fach]![0] + F[fach]![2] - r.buchstuetze.breite - 4;
    if (x + book.breite > end) {
      fach += 1;
      if (fach >= F.length) {
        fach = 0;
        shelves.push([]);
      }
      x = F[fach]![0] + 4;
    }
    shelves[shelves.length - 1]!.push({ rec, n: i + 1, book, shelf: shelves.length - 1, fach, x });
    x += book.breite;
  });
  return shelves;
}

/** Bestmarke für das goldene Lesezeichen: am Ziel mit den wenigsten Abschieden, sonst die längste Reise */
function bestOf(list: RunRecord[]): string | null {
  if (list.length < 2) return null;
  const sorted = [...list].sort((a, b) => Number(b.won) - Number(a.won) || (a.won ? a.farewells - b.farewells : b.days - a.days) || b.driven - a.driven);
  return sorted[0]!.id;
}

/**
 * Das Regal mit einem Buchrücken je Reise (das neueste zuletzt); die gerade beendete trägt das rote Lesezeichen, die beste
 * das goldene. Mehr Reisen, als in ein Regal passen: das neueste Regal zuerst, geblättert wird mit zwei Knöpfen
 */
export function regalHtml(list: RunRecord[], current?: string, page?: number): string {
  const r = loadEnde().regal;
  if (!r) return '';
  const shelves = layout(r, list);
  const p = Math.max(0, Math.min(shelves.length - 1, page ?? shelves.length - 1));
  const best = bestOf(list);
  const books = (shelves[p] ?? [])
    .map((b) => {
      const top = r.regal.boden_y[b.fach]! - b.book.hoehe;
      const [fx, fy, fw, fh] = b.book.feld;
      const mark = b.rec.id === current ? 'lesezeichen-rot.png' : b.rec.id === best ? 'lesezeichen-gold.png' : null;
      const tip = `Reise ${b.n} · ${b.rec.date}\n${b.rec.won ? 'Am Ziel' : `Bis Tag ${b.rec.days}`} · ${b.rec.fights} ${b.rec.fights === 1 ? 'Kampf' : 'Kämpfe'} · ${b.rec.farewells ? `${b.rec.farewells} ${b.rec.farewells === 1 ? 'Abschied' : 'Abschiede'}` : 'kein Abschied'}\n„${b.rec.line}“`;
      return `<button class="book${b.rec.id === current ? ' now' : ''}" data-book="${esc(b.rec.id)}" data-tip="${esc(tip)}" style="left:${b.x}px;top:${top}px;width:${b.book.breite}px;height:${b.book.hoehe}px;background-image:url(art/ende/${b.book.datei})" aria-label="${esc(`Reise ${b.n}`)}">
          <span style="left:${fx}px;top:${fy}px;width:${fw}px;height:${fh}px;color:${b.book.schrift}" class="${String(b.n).length > 2 ? 'tall' : ''}">${b.n}</span>
          ${mark ? `<i class="mark" style="background-image:url(art/ende/${mark})"></i>` : ''}
        </button>`;
    })
    .join('');
  const last = shelves[p]?.[shelves[p]!.length - 1];
  const stuetze = last ? `<i class="stuetze" style="left:${last.x + last.book.breite}px;top:${r.regal.boden_y[last.fach]! - r.buchstuetze.hoehe}px;background-image:url(art/ende/${r.buchstuetze.datei})"></i>` : '';
  const [sx, sy, sw, sh] = r.regal.schild;
  const nav = shelves.length > 1 ? `<div class="regal-nav"><button data-regal="${p - 1}" ${p === 0 ? 'disabled' : ''}>‹ älter</button><button data-regal="${p + 1}" ${p >= shelves.length - 1 ? 'disabled' : ''}>neuer ›</button></div>` : '';
  return `<div class="regal" style="width:${r.regal.groesse[0]}px;height:${r.regal.groesse[1]}px">
      <span class="schild" style="left:${sx}px;top:${sy}px;width:${sw}px;height:${sh}px">${p + 1}</span>
      ${list.length ? books + stuetze : '<p class="regal-leer">Noch keine Reise in der Bibliothek.</p>'}
    </div>${nav}`;
}
